import { PlottableForecast } from '../types';
import { extractWeatherState, WeatherState } from './weatherConditionUtils';

/**
 * Represents a time slot in the weather summary
 */
export interface WeatherSummarySlot {
  /** The time for this slot */
  time: Date;
  /** Weather state for this time slot */
  weatherState: WeatherState;
  /** The index in the original forecast data */
  dataIndex: number;
}

/**
 * Configuration for grouping forecast data into time slots
 */
export interface TimeSlotConfig {
  /** Interval between time slots in hours (e.g., 3 for 3-hourly, 6 for 6-hourly) */
  intervalHours: number;
  /** Maximum number of slots to return */
  maxSlots?: number;
  /** Optional function to get the local hour in user's timezone (for day/night determination) */
  getLocalHour?: (date: Date) => number;
}

/**
 * Groups forecast data into time slots at regular intervals
 *
 * @param forecast - The forecast data to process
 * @param config - Configuration for time slot grouping
 * @returns Array of weather summary slots
 */
export function groupForecastIntoTimeSlots(
  forecast: PlottableForecast,
  config: TimeSlotConfig = { intervalHours: 3 },
): WeatherSummarySlot[] {
  const { intervalHours, maxSlots, getLocalHour } = config;
  const slots: WeatherSummarySlot[] = [];

  if (!forecast.time || forecast.time.length === 0) {
    return slots;
  }

  // Start from the first time slot
  let currentIndex = 0;

  // Process time slots at the specified interval
  while (currentIndex < forecast.time.length) {
    const currentTime = forecast.time[currentIndex];

    // Extract weather state for this time point, passing function to get local hour
    const weatherState = extractWeatherState(
      forecast.data,
      currentIndex,
      currentTime,
      getLocalHour,
    );

    // Only add slot if we have valid weather state
    if (weatherState !== null) {
      slots.push({
        time: currentTime,
        weatherState,
        dataIndex: currentIndex,
      });

      // Stop if we've reached the maximum number of slots
      if (maxSlots && slots.length >= maxSlots) {
        break;
      }
    }

    // Move to next interval
    // Find the next time point that's at least intervalHours ahead
    const targetTime = new Date(currentTime.getTime() + intervalHours * 60 * 60 * 1000);

    // Find the closest time point to the target
    let nextIndex = currentIndex + 1;
    while (
      nextIndex < forecast.time.length &&
      forecast.time[nextIndex].getTime() < targetTime.getTime()
    ) {
      nextIndex++;
    }

    // If we've gone past the end, stop
    if (nextIndex >= forecast.time.length) {
      break;
    }

    currentIndex = nextIndex;
  }

  return slots;
}

/**
 * Calculates statistics across multiple slots
 * Useful for showing aggregate information
 *
 * @param slots - Array of weather summary slots
 * @returns Statistics object with min/max/avg values
 */
export function calculateSlotStatistics(slots: WeatherSummarySlot[]) {
  if (slots.length === 0) {
    return null;
  }

  const temperatures = slots.map((slot) => slot.weatherState.temperature);
  const precipitations = slots.map((slot) => slot.weatherState.precipitation);
  const windSpeeds = slots.map((slot) => slot.weatherState.windSpeed);

  return {
    temperature: {
      min: Math.min(...temperatures),
      max: Math.max(...temperatures),
      avg: Math.round(temperatures.reduce((a, b) => a + b, 0) / temperatures.length),
    },
    precipitation: {
      total: precipitations.reduce((a, b) => a + b, 0),
      max: Math.max(...precipitations),
    },
    windSpeed: {
      max: Math.max(...windSpeeds),
      avg: Math.round(windSpeeds.reduce((a, b) => a + b, 0) / windSpeeds.length),
    },
  };
}

/**
 * Filters slots to show only specific times of day (e.g., 6AM, 9AM, 12PM, 3PM, 6PM, 9PM)
 * This creates a more user-friendly display with consistent time points
 *
 * @param slots - Array of weather summary slots
 * @param targetHours - Array of hours (0-23) to show (e.g., [6, 9, 12, 15, 18, 21])
 * @returns Filtered array of slots closest to target hours
 */
export function filterSlotsByTargetHours(
  slots: WeatherSummarySlot[],
  targetHours: number[] = [6, 9, 12, 15, 18, 21],
): WeatherSummarySlot[] {
  if (slots.length === 0) {
    return [];
  }

  const result: WeatherSummarySlot[] = [];
  const usedIndices = new Set<number>();

  for (const targetHour of targetHours) {
    // Find the slot closest to this target hour
    let closestSlot: WeatherSummarySlot | null = null;
    let minDiff = Infinity;
    let closestIndex = -1;

    slots.forEach((slot, index) => {
      if (usedIndices.has(index)) {
        return; // Skip already used slots
      }

      const slotHour = slot.time.getUTCHours();
      // Calculate hour difference (considering wrap-around)
      let diff = Math.abs(slotHour - targetHour);
      if (diff > 12) {
        diff = 24 - diff;
      }

      if (diff < minDiff) {
        minDiff = diff;
        closestSlot = slot;
        closestIndex = index;
      }
    });

    if (closestSlot && minDiff <= 3) {
      // Only include if within 3 hours of target
      result.push(closestSlot);
      usedIndices.add(closestIndex);
    }
  }

  return result;
}

/**
 * Groups slots by day for multi-day forecasts
 *
 * @param slots - Array of weather summary slots
 * @returns Object mapping date strings to arrays of slots
 */
export function groupSlotsByDay(slots: WeatherSummarySlot[]): Record<string, WeatherSummarySlot[]> {
  const grouped: Record<string, WeatherSummarySlot[]> = {};

  slots.forEach((slot) => {
    const dateKey = slot.time.toISOString().split('T')[0]; // Get YYYY-MM-DD
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(slot);
  });

  return grouped;
}

/**
 * Gets the predominant (most common) weather condition across slots
 *
 * @param slots - Array of weather summary slots
 * @returns The most common weather condition
 */
export function getPredominantCondition(slots: WeatherSummarySlot[]): string {
  if (slots.length === 0) {
    return 'clear';
  }

  const conditionCounts: Record<string, number> = {};

  slots.forEach((slot) => {
    const condition = slot.weatherState.condition;
    conditionCounts[condition] = (conditionCounts[condition] || 0) + 1;
  });

  // Find the condition with the highest count
  let maxCount = 0;
  let predominantCondition = 'clear';

  Object.entries(conditionCounts).forEach(([condition, count]) => {
    if (count > maxCount) {
      maxCount = count;
      predominantCondition = condition;
    }
  });

  return predominantCondition;
}

/**
 * Determines if any precipitation is expected in the slots
 *
 * @param slots - Array of weather summary slots
 * @param threshold - Minimum precipitation amount to consider (mm)
 * @returns True if any slot has precipitation above threshold
 */
export function hasPrecipitation(slots: WeatherSummarySlot[], threshold: number = 0.1): boolean {
  return slots.some((slot) => slot.weatherState.precipitation > threshold);
}

/**
 * Formats a time slot for display
 *
 * @param slot - Weather summary slot
 * @param formatInTimezone - Function to format date in user's timezone
 * @returns Formatted time string (e.g., "6:00 AM", "15:00")
 */
export function formatSlotTime(
  slot: WeatherSummarySlot,
  formatInTimezone?: (date: Date, format: string) => string,
): string {
  if (formatInTimezone) {
    return formatInTimezone(slot.time, 'h:mm a');
  }

  // Fallback to simple UTC formatting
  const hours = slot.time.getUTCHours();
  const minutes = slot.time.getUTCMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;

  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}
